import { User } from 'oidc-client-ts';
import { createApiClient } from '../oidc-api';
import { ApplicationDto, ListApplicationDto, ParameterDto, SystemMessageDto, ListEmbeddingDto, PromptParameterDto, ToolDto, ListToolDto, ToolType, ToolStatus } from './types';

/**
 * OIDC-based Applications Service
 * All methods require a User object from the OIDC context
 */

// Helper for retry mechanism
export const withRetry = async <T>(
  promiseFn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await promiseFn();
    } catch (error) {
      lastError = error;
      // Wait before retrying
      if (attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
};

/**
 * Fetch a list of applications with pagination
 */
export const getApplications = async (
  user: User | null,
  pageNumber: number = 1, 
  pageSize: number = 10
): Promise<ListApplicationDto> => {
  const api = createApiClient(user);
  return api.get<ListApplicationDto>(`/v1/applications?page_number=${pageNumber}&page_size=${pageSize}`);
};

/**
 * Fetch a single application by ID
 * Using retry mechanism for more reliable fetching of individual application details
 */
export const getApplicationById = async (user: User | null, id: string): Promise<ApplicationDto> => {
  const api = createApiClient(user);
  return withRetry(() => api.get<ApplicationDto>(`/v1/applications/${id}`));
};

/**
 * Create a new application
 */
export const createApplication = async (
  user: User | null,
  applicationData: ApplicationDto
): Promise<ApplicationDto> => {
  const api = createApiClient(user);
  return api.post<ApplicationDto>('/v1/applications', applicationData);
};

/**
 * Update an existing application
 */
export const updateApplication = async (
  user: User | null,
  id: string,
  applicationData: ApplicationDto
): Promise<ApplicationDto> => {
  const api = createApiClient(user);
  return api.put<ApplicationDto>(`/v1/applications/${id}`, applicationData);
};

/**
 * Get application parameters
 * Using retry mechanism for more reliable fetching of parameters
 */
export const getApplicationParameters = async (
  user: User | null,
  applicationId: string
): Promise<ParameterDto[]> => {
  const api = createApiClient(user);
  return withRetry(() => 
    api.get<ParameterDto[]>(`/v1/applications/${applicationId}/parameters`)
  );
};

/**
 * Create application parameter
 */
export const createApplicationParameter = async (
  user: User | null,
  applicationId: string, 
  parameterData: ParameterDto
): Promise<ParameterDto> => {
  const api = createApiClient(user);
  return api.post<ParameterDto>(`/v1/applications/${applicationId}/parameters`, parameterData);
};

/**
 * Update application parameter
 */
export const updateApplicationParameter = async (
  user: User | null,
  applicationId: string,
  parameterId: string,
  parameterData: ParameterDto
): Promise<ParameterDto> => {
  const api = createApiClient(user);
  return api.put<ParameterDto>(
    `/v1/applications/${applicationId}/parameters/${parameterId}`, 
    parameterData
  );
};

/**
 * Delete application parameter
 */
export const deleteApplicationParameter = async (
  user: User | null,
  applicationId: string,
  parameterId: string
): Promise<void> => {
  const api = createApiClient(user);
  return api.delete<void>(`/v1/applications/${applicationId}/parameters/${parameterId}`);
};

/**
 * Get application prompts (system messages)
 */
export const getApplicationPrompts = async (
  user: User | null,
  applicationId: string,
  userStatus?: string,
  pageNumber: number = 1,
  pageSize: number = 10
): Promise<SystemMessageDto[]> => {
  const api = createApiClient(user);
  let url = `/v1/applications/${applicationId}/prompts?page_number=${pageNumber}&page_size=${pageSize}`;
  if (userStatus) {
    url += `&user_status=${userStatus}`;
  }
  const results = await api.get<SystemMessageDto[]>(url);
  
  // Ensure message field is populated from system_message if needed
  return results.map(prompt => {
    if (prompt.system_message && !prompt.message) {
      return { ...prompt, message: prompt.system_message };
    }
    return prompt;
  });
};

/**
 * Create a new application prompt (system message)
 */
export const createApplicationPrompt = async (
  user: User | null,
  applicationId: string,
  promptData: SystemMessageDto
): Promise<SystemMessageDto> => {
  const api = createApiClient(user);
  
  // Preparar dados para envio à API
  const apiData: any = {
    ...promptData
  };

  // Garantir que campos obrigatórios estejam preenchidos
  if (promptData.message) {
    apiData.system_message = promptData.message;
  }

  // Certifique-se de que provider_name está definido (campo obrigatório)
  if (!apiData.provider_name) {
    apiData.provider_name = 'ANTHROPIC'; // valor padrão
  }

  // Certifique-se de que os campos obrigatórios estão presentes
  if (!apiData.application_id) {
    apiData.application_id = applicationId;
  }

  console.log("Creating new prompt:", apiData);
  
  return api.post<SystemMessageDto>(`/v1/applications/${applicationId}/prompts`, apiData);
};

/**
 * Update an existing prompt (system message)
 */
export const updateApplicationPrompt = async (
  user: User | null,
  applicationId: string,
  promptId: string,
  promptData: SystemMessageDto
): Promise<SystemMessageDto> => {
  const api = createApiClient(user);
  
  // Preparar dados para envio à API
  const apiData: any = {
    ...promptData
  };

  // Se temos message mas não system_message, atualiza system_message com message
  if (promptData.message && !promptData.system_message) {
    apiData.system_message = promptData.message;
  }

  // Se message foi atualizado pelo UI e está diferente de system_message, atualize system_message
  if (promptData.message && promptData.system_message &&
      promptData.message !== promptData.system_message) {
    apiData.system_message = promptData.message;
  }

  // Garantir campos obrigatórios conforme Swagger
  if (!apiData.provider_name) {
    apiData.provider_name = 'ANTHROPIC';
  }

  if (!apiData.application_id) {
    apiData.application_id = applicationId;
  }

  if (!apiData.user_status) {
    // Alguns prompts podem ser para todos os status de usuário
    // apiData.user_status = 'ACTIVATED'; // Valor padrão se necessário
  }

  console.log("Sending update to API:", apiData);
  
  return api.put<SystemMessageDto>(
    `/v1/applications/${applicationId}/prompts/${promptId}`, 
    apiData
  );
};

/**
 * Delete an application prompt (system message)
 */
export const deleteApplicationPrompt = async (
  user: User | null,
  applicationId: string,
  promptId: string
): Promise<void> => {
  const api = createApiClient(user);
  return api.delete<void>(`/v1/applications/${applicationId}/prompts/${promptId}`);
};

/**
 * Get prompt parameters
 * Returns all available parameters that can be used in prompt templates
 */
export const getPromptParameters = async (
  user: User | null
): Promise<PromptParameterDto[]> => {
  const api = createApiClient(user);
  try {
    // Tentar obter da API simplificada primeiro
    console.log("Fetching parameters from simplified API...");
    const simpleParams = await withRetry(() => api.get<string[]>(`/v1/prompt-parameters`));
    
    console.log("Parameters fetched successfully:", simpleParams);
    
    // Converter array de strings para formato PromptParameterDto
    if (Array.isArray(simpleParams)) {
      return simpleParams.map(paramName => ({
        name: paramName,
        category: paramName.includes("user") ? "User" : 
                 paramName.includes("date") ? "DateTime" : 
                 paramName.includes("time") ? "DateTime" : 
                 "General"
      }));
    } else {
      console.warn("API returned unexpected format:", simpleParams);
      throw new Error("Invalid parameters format");
    }
  } catch (error) {
    console.error("Error fetching prompt parameters:", error);
    // Return fallback parameters for development/testing
    return [
      {
        name: "firstName",
        category: "User"
      },
      {
        name: "lastName", 
        category: "User"
      },
      {
        name: "email",
        category: "User"
      },
      {
        name: "currentDate",
        category: "DateTime"
      },
      {
        name: "companyName",
        category: "Company"
      }
    ];
  }
};

/**
 * Get available user statuses
 * Returns all available user status values
 */
export const getUserStatuses = async (
  user: User | null
): Promise<string[]> => {
  const api = createApiClient(user);
  try {
    console.log("Fetching user statuses from API...");
    const statuses = await withRetry(() => api.get<string[]>(`/v1/user-status`));
    
    console.log("User statuses fetched successfully:", statuses);
    
    if (Array.isArray(statuses)) {
      return statuses;
    } else {
      console.warn("API returned unexpected format for user statuses:", statuses);
      throw new Error("Invalid user statuses format");
    }
  } catch (error) {
    console.error("Error fetching user statuses:", error);
    // Return fallback statuses for development/testing
    return [
      "NEW",
      "ACTIVE", 
      "INACTIVE",
      "BLOCKED",
      "ONBOARDING_IN_PROGRESS"
    ];
  }
};

/**
 * Get available tools for prompts
 * Returns all available tools that can be used in prompts
 */
export const getPromptTools = async (
  user: User | null
): Promise<string[]> => {
  const api = createApiClient(user);
  try {
    console.log("Fetching prompt tools from API...");
    const tools = await withRetry(() => api.get<string[]>(`/v1/prompt-tools`));
    
    console.log("Prompt tools fetched successfully:", tools);
    
    if (Array.isArray(tools)) {
      return tools;
    } else {
      console.warn("API returned unexpected format for prompt tools:", tools);
      throw new Error("Invalid prompt tools format");
    }
  } catch (error) {
    console.error("Error fetching prompt tools:", error);
    // Return fallback tools for development/testing
    return [
      "VerifyPendingChargesFunctionCall",
      "RagSearchTool"
    ];
  }
};

/**
 * Get specific prompt with full details using application-scoped endpoint
 */
export const getPromptById = async (
  user: User | null,
  applicationId: string,
  promptId: string
): Promise<SystemMessageDto> => {
  const api = createApiClient(user);
  try {
    // First try to get the prompt directly by ID
    const result = await withRetry(() => 
      api.get<SystemMessageDto>(`/v1/applications/${applicationId}/prompts/${promptId}`)
    );
    console.log("Prompt fetched successfully:", result);
    
    // Map response fields if using new API format (with system_message field)
    if (result.system_message && !result.message) {
      result.message = result.system_message;
    }
    
    return result;
  } catch (error) {
    console.error(`Error fetching prompt by ID: ${promptId}`, error);
    
    // Fallback method: get all prompts and find the one with matching ID
    try {
      console.log("Trying fallback: fetching all prompts");
      const allPrompts = await getApplicationPrompts(user, applicationId);
      const foundPrompt = allPrompts.find(p => p.id === promptId);
      
      if (foundPrompt) {
        console.log("Found prompt in list:", foundPrompt);
        return foundPrompt;
      } else {
        throw new Error("Prompt not found in application's prompt list");
      }
    } catch (fallbackError) {
      console.error("Fallback also failed:", fallbackError);
      throw new Error(`Could not retrieve prompt with ID: ${promptId}`);
    }
  }
};

/**
 * Get prompt by ID using the direct prompts endpoint
 * This endpoint returns more detailed information about the prompt
 */
export const getPromptDirectById = async (
  user: User | null,
  promptId: string
): Promise<SystemMessageDto> => {
  const api = createApiClient(user);
  try {
    // Get the prompt directly from the prompts endpoint
    const result = await withRetry(() => 
      api.get<SystemMessageDto>(`/v1/prompts/${promptId}`)
    );
    console.log("Prompt fetched directly successfully:", result);
    
    // Map response fields if using system_message but not message
    if (result.system_message && !result.message) {
      result.message = result.system_message;
    }
    
    // Garantir que o campo is_active seja explicitamente um boolean
    if (result.is_active === undefined) {
      result.is_active = true; // Default para true se não especificado
    } else if (typeof result.is_active === 'string') {
      // Usar type assertion para informar ao TypeScript que é uma string
      const isActiveStr = result.is_active as string;
      result.is_active = isActiveStr.toLowerCase() === 'true';
    }
    
    // Garantir que o campo with_rag seja explicitamente um boolean
    if (result.with_rag === undefined) {
      result.with_rag = false; // Default para false se não especificado
    } else if (typeof result.with_rag === 'string') {
      // Usar type assertion para informar ao TypeScript que é uma string
      const withRagStr = result.with_rag as string;
      result.with_rag = withRagStr.toLowerCase() === 'true';
    }
    
    // Converter temperatura para número se vier como string
    if (typeof result.temperature === 'string') {
      result.temperature = parseFloat(result.temperature);
    }
    
    // Garantir que tools seja um array
    if (result.tools === undefined) {
      result.tools = [];
    } else if (typeof result.tools === 'string') {
      try {
        // Usar type assertion para converter para string e depois parsear para array
        const toolsStr = result.tools as string;
        const parsedTools = JSON.parse(toolsStr);
        
        // Verificar se o resultado parseado é um array
        if (Array.isArray(parsedTools)) {
          result.tools = parsedTools;
        } else {
          // Se não for array, criar um array com o valor único
          result.tools = [toolsStr];
        }
      } catch (e) {
        console.error("Erro ao processar ferramentas:", e);
        result.tools = [];
      }
    }
    
    return result;
  } catch (error) {
    console.error(`Error fetching prompt directly by ID: ${promptId}`, error);
    throw new Error(`Could not retrieve prompt with ID: ${promptId}`);
  }
};

/**
 * Get embeddings with pagination and filtering options
 * Using retry mechanism for more reliable fetching of embeddings
 */
export const getApplicationEmbeddings = async (
  user: User | null,
  applicationId: string,
  pageNumber: number = 1,
  pageSize: number = 10,
  query?: string,
  top_k?: number,
  similarity_threshold?: number
): Promise<ListEmbeddingDto> => {
  const api = createApiClient(user);
  
  // Start with base URL params that will always be included
  const params = new URLSearchParams({
    page_number: pageNumber.toString(),
    page_size: pageSize.toString()
  });
  
  // Add application ID as a query parameter
  params.append('application_id', applicationId);
  
  // Add optional parameters if provided
  if (query && query.trim() !== '') {
    params.append('query', query);
  }
  
  if (top_k !== undefined && top_k > 0) {
    params.append('top_k', top_k.toString());
  }
  
  if (similarity_threshold !== undefined && similarity_threshold > 0) {
    params.append('similarity_threshold', similarity_threshold.toString());
  }
  
  // Use the base embeddings endpoint
  const url = `/v1/embeddings?${params.toString()}`;
  
  return withRetry(() => api.get<ListEmbeddingDto>(url));
};

/**
 * Mock data for Tools (since we don't have a real API endpoint yet)
 */
const mockTools: ToolDto[] = [
  {
    id: '1',
    name: 'calculate_metrics',
    description: 'Calcula métricas de desempenho baseadas em dados de entrada',
    type: ToolType.FUNCTION,
    status: ToolStatus.ACTIVE,
    script: `# Função para calcular métricas de desempenho
import json
import statistics

def main(context):
    """
    Calcula métricas estatísticas dos dados fornecidos.
    
    Args:
        context (dict): Contexto com os dados para análise
        
    Returns:
        dict: Métricas calculadas
    """
    data = context.get('data', [])
    
    if not data:
        return {
            "status": "error",
            "message": "No data provided"
        }
    
    # Calcular métricas
    metrics = {
        "count": len(data),
        "mean": statistics.mean(data),
        "median": statistics.median(data),
        "std_dev": statistics.stdev(data) if len(data) > 1 else 0,
        "min": min(data),
        "max": max(data)
    }
    
    return {
        "status": "success",
        "metrics": metrics
    }
`,
    created_at: '2024-01-15T10:00:00Z',
    updated_at: '2024-01-15T10:00:00Z'
  },
  {
    id: '2',
    name: 'process_text',
    description: 'Processa e analisa texto usando técnicas de NLP',
    type: ToolType.FUNCTION,
    status: ToolStatus.ACTIVE,
    script: `# Função de processamento de texto
import re
from collections import Counter

def main(context):
    """
    Analisa texto e retorna estatísticas.
    
    Args:
        context (dict): Contexto com o texto para análise
        
    Returns:
        dict: Análise do texto
    """
    text = context.get('text', '')
    
    if not text:
        return {
            "status": "error",
            "message": "No text provided"
        }
    
    # Análise básica
    words = re.findall(r'\\w+', text.lower())
    word_count = len(words)
    char_count = len(text)
    
    # Palavras mais comuns
    word_freq = Counter(words)
    top_words = word_freq.most_common(10)
    
    return {
        "status": "success",
        "analysis": {
            "word_count": word_count,
            "char_count": char_count,
            "unique_words": len(set(words)),
            "top_words": [{"word": w, "count": c} for w, c in top_words]
        }
    }
`,
    created_at: '2024-01-10T08:30:00Z',
    updated_at: '2024-01-10T08:30:00Z'
  },
  {
    id: '3',
    name: 'validate_data',
    description: 'Valida estrutura e conteúdo de dados JSON',
    type: ToolType.FUNCTION,
    status: ToolStatus.ACTIVE,
    script: `# Função de validação de dados
import json

def main(context):
    """
    Valida a estrutura e conteúdo de dados JSON.
    
    Args:
        context (dict): Contexto com dados e regras de validação
        
    Returns:
        dict: Resultado da validação
    """
    data = context.get('data', {})
    rules = context.get('rules', {})
    
    errors = []
    
    # Validar campos obrigatórios
    required_fields = rules.get('required_fields', [])
    for field in required_fields:
        if field not in data:
            errors.append(f"Campo obrigatório ausente: {field}")
    
    # Validar tipos de dados
    field_types = rules.get('field_types', {})
    for field, expected_type in field_types.items():
        if field in data:
            actual_type = type(data[field]).__name__
            if actual_type != expected_type:
                errors.append(f"Tipo incorreto para {field}: esperado {expected_type}, encontrado {actual_type}")
    
    # Validar valores mínimos/máximos
    min_values = rules.get('min_values', {})
    max_values = rules.get('max_values', {})
    
    for field, min_val in min_values.items():
        if field in data and isinstance(data[field], (int, float)):
            if data[field] < min_val:
                errors.append(f"{field} abaixo do mínimo: {data[field]} < {min_val}")
    
    for field, max_val in max_values.items():
        if field in data and isinstance(data[field], (int, float)):
            if data[field] > max_val:
                errors.append(f"{field} acima do máximo: {data[field]} > {max_val}")
    
    return {
        "status": "success" if not errors else "error",
        "valid": len(errors) == 0,
        "errors": errors,
        "data": data
    }
`,
    created_at: '2023-12-01T14:20:00Z',
    updated_at: '2024-01-05T09:15:00Z'
  }
];

/**
 * Get application tools with pagination
 */
export const getApplicationTools = async (
  user: User | null,
  applicationId: string,
  pageNumber: number = 1,
  pageSize: number = 10
): Promise<ListToolDto> => {
  // For now, return mock data
  // In production, this would call: api.get<ListToolDto>(`/v1/applications/${applicationId}/tools?page_number=${pageNumber}&page_size=${pageSize}`)
  
  const start = (pageNumber - 1) * pageSize;
  const end = start + pageSize;
  const paginatedTools = mockTools.slice(start, end);
  
  return Promise.resolve({
    data: paginatedTools,
    current_page: pageNumber,
    total_pages: Math.ceil(mockTools.length / pageSize),
    total_items: mockTools.length
  });
};

/**
 * Get a single tool by ID
 */
export const getToolById = async (
  user: User | null,
  applicationId: string,
  toolId: string
): Promise<ToolDto> => {
  // In production: api.get<ToolDto>(`/v1/applications/${applicationId}/tools/${toolId}`)
  
  const tool = mockTools.find(t => t.id === toolId);
  if (!tool) {
    throw new Error(`Tool with ID ${toolId} not found`);
  }
  
  return Promise.resolve(tool);
};

/**
 * Create a new tool
 */
export const createApplicationTool = async (
  user: User | null,
  applicationId: string,
  toolData: ToolDto
): Promise<ToolDto> => {
  // In production: api.post<ToolDto>(`/v1/applications/${applicationId}/tools`, toolData)
  
  const newTool: ToolDto = {
    ...toolData,
    id: Date.now().toString(),
    application_id: applicationId,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: user?.profile?.sub,
    version: 1
  };
  
  mockTools.push(newTool);
  return Promise.resolve(newTool);
};

/**
 * Update an existing tool
 */
export const updateApplicationTool = async (
  user: User | null,
  applicationId: string,
  toolId: string,
  toolData: ToolDto
): Promise<ToolDto> => {
  // In production: api.put<ToolDto>(`/v1/applications/${applicationId}/tools/${toolId}`, toolData)
  
  const index = mockTools.findIndex(t => t.id === toolId);
  if (index === -1) {
    throw new Error(`Tool with ID ${toolId} not found`);
  }
  
  const updatedTool: ToolDto = {
    ...mockTools[index],
    ...toolData,
    id: toolId,
    updated_at: new Date().toISOString(),
    updated_by: user?.profile?.sub,
    version: (mockTools[index].version || 0) + 1
  };
  
  mockTools[index] = updatedTool;
  return Promise.resolve(updatedTool);
};

/**
 * Delete a tool
 */
export const deleteApplicationTool = async (
  user: User | null,
  applicationId: string,
  toolId: string
): Promise<void> => {
  // In production: api.delete<void>(`/v1/applications/${applicationId}/tools/${toolId}`)
  
  const index = mockTools.findIndex(t => t.id === toolId);
  if (index === -1) {
    throw new Error(`Tool with ID ${toolId} not found`);
  }
  
  mockTools.splice(index, 1);
  return Promise.resolve();
};